import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo } from '@/mocks/movies';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
              <span className="text-foreground-200 font-medium">关于我们</span>
            </nav>

            {/* Hero */}
            <section className="text-center mb-14 md:mb-20" aria-label="品牌介绍">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-50 mb-4">
                {aboutInfo.slogan}
              </h1>
              <p className="text-base md:text-lg text-foreground-400 max-w-3xl mx-auto leading-relaxed">
                {aboutInfo.introduction}
              </p>
            </section>

            {/* Stats */}
            <section className="mb-14 md:mb-20" aria-label="平台数据">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-primary-400 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Brand Story */}
            <section className="mb-14 md:mb-20" aria-label="品牌故事">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-6 text-center">
                品牌故事
              </h2>
              <div className="max-w-3xl mx-auto bg-background-100 rounded-2xl border border-background-200/50 p-6 md:p-10">
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
                  {aboutInfo.story}
                </p>
              </div>
            </section>

            {/* Features */}
            <section className="mb-14 md:mb-20" aria-label="核心优势">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-6 text-center">
                为什么选择我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {aboutInfo.features.map((feature) => (
                  <div key={feature.title} className="p-6 rounded-xl bg-background-100 border border-background-200/50 hover:border-primary-500/20 transition-all duration-300">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 mb-4">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-50 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reviews */}
            <section id="reviews" className="mb-14 md:mb-20" aria-label="用户口碑">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {aboutInfo.reviews.map((review, index) => (
                  <div key={index} className="p-6 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < review.rating ? 'ri-star-fill text-primary-400' : 'ri-star-fill text-foreground-700'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-300 leading-relaxed mb-4">
                      &ldquo;{review.text}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <span className="text-xs font-bold text-primary-400">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-foreground-200">{review.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section id="contact" className="pb-14 md:pb-20" aria-label="联系方式">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-6 text-center">
                联系我们
              </h2>
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                      <p className="text-sm text-foreground-200">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">微信公众号</p>
                      <p className="text-sm text-foreground-200">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                      <i className="ri-weibo-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">官方微博</p>
                      <p className="text-sm text-foreground-200">{aboutInfo.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">客服电话</p>
                      <p className="text-sm text-foreground-200">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.chunyecinema.com/" },
            { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://www.chunyecinema.com/about" }
          ]
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于春夜影院",
          "url": "https://www.chunyecinema.com/about",
          "description": aboutInfo.introduction,
          "isPartOf": { "@type": "WebSite", "name": "春夜影院", "url": "https://www.chunyecinema.com" }
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "春夜影院是否需要付费？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "春夜影院所有内容完全免费，无需付费会员，无需注册登录，打开即可观看。"
              }
            },
            {
              "@type": "Question",
              "name": "春夜影院支持哪些画质？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "春夜影院提供1080P蓝光画质，部分热门内容支持4K超清画质，确保最佳观影体验。"
              }
            },
            {
              "@type": "Question",
              "name": "春夜影院是否支持手机观看？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "春夜影院完美适配电脑、平板、手机等多种设备，随时随地畅享观影乐趣。"
              }
            },
            {
              "@type": "Question",
              "name": "春夜影院的内容更新速度如何？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "春夜影院24小时不间断更新，第一时间上线最新电影、电视剧、综艺和动漫内容。"
              }
            }
          ]
        }) }} />
      </main>

      <Footer />
    </div>
  );
}